import React, {useRef, useState, useEffect} from 'react';
import {
    Card,
    CardBody,
    CardTitle,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Collapse,
    InputGroup,
    Button, InputGroupAddon
} from 'reactstrap';
import {ErrorMessage, useForm} from "react-hook-form";
import * as Yup from "yup";
import Switch from "react-bootstrap-switch";
import {connect, useSelector} from "react-redux";

// components
import SelectGeneric from '../../../components/select/selectGeneric';
import { MapPickerModal } from "../../../components/map/MapPickerModal";
import { LogHistoryModal } from "../../../components/logHistory/logHistoryModal";
import ImagePicker from "../../../components/imagePickerComponents/ImagePicker";

// labels
import {TT_CLOSE, TT_DELETE, TT_HISTORY, TT_SAVE} from "../../../components/i18n/toolTips";
import {
    FORM,
    INTERNATIONAL, LB_CLOSING_TIME, LB_FLOORS, LB_OPENING_TIME,
    LB_TOTAL_SUITS,
    REQUIRED
} from "../../../components/i18n/i18n";

// services & providers
import { placeProvider } from "../../../providers/placeProvider";
import { placeService } from "../../../jwt/_services/hotel/placeService";
import { placeMediaService } from "../../../jwt/_services/core/placeMediaService";
import { typePlaceProvider } from "../../../providers/typePlaceProvider";
import { hotelCategoryProvider } from "../../../providers/hotelCategoryProvider";

// actions
import { syncGallery} from "../../../redux/actions/hotel/placeActions";
import {getStateByPath} from "../../../redux/selectors";
import {genericIndexedActions} from "../../../redux/actions/generics/GenericIndexedActions";
import {GenericButton} from "../../../components/buttons/customButtons";
import Separator from "../../../components/Separator";
import TranslateButton from "../../../components/buttons/TranslateButton";
import {FL_CATEGORY, FL_CODE, FL_RESERVATION_NEEDED, FL_STATUS} from "../../../components/i18n/formLabels";
import {PH_CODE} from "../../../components/i18n/placeHolders";
import PlaceLangs from "./_langs";
import PlaceRestaurantLangs from "./_langsRestaurant";
import {setForm, setItem} from "../../../redux/actions/generics/IndexedActions";
import {ConfirmDelete} from "../../../components/confirmAlert/ConfirmDelete";
import {getCurrentDateForCodeField} from "../../../util/DateTools";

const PlaceForm = ( props ) => {
    const componentState = useSelector(state => getStateByPath(state, props.path));

    const { path } = componentState;
    const { app, udn, editMode, domainObject, mapConfig} = componentState?.data;

    const translatableLangs = domainObject?.translatedLangs || []

    const [status, setStatus] = useState( domainObject?.status || true);
    const [outsideUDN, setOutsideUDN] = useState( domainObject?.outsideUDN || false);
    const [weather, setWeather] = useState( domainObject?.weather || false);
    const [needReservation, setNeedReservation] = useState( domainObject?.needReservation || false);
    const [category, setCategory] = useState( domainObject?.category || null);
    const [contactCategory, setContactCategory] = useState( domainObject?.contactCategory || null);
    const [parentPlace, seParentPlace] = useState( domainObject?.parentPlace || null);
    const [typePlace, setTypePlace] = useState( domainObject?.typePlace || null);
    const [position, setPosition] = useState(editMode ? {
        lat: parseFloat(domainObject?.latitude),
        lng: parseFloat(domainObject?.longitude)
    } : mapConfig?.mapOptions?.center );

    const [langs, setLangs] = useState(domainObject?.langs || []);

    const [showLangRest, setShowLangRest] = useState(  domainObject?.langsRestaurant?.length > 0 );
    const [langsRestaurant, setLangsRestaurant] = useState(domainObject?.langsRestaurant || []);
    const [langRestaurantCollapse, setLangRestaurantCollapse] = useState(true)
    const [paxes, setPaxes] = useState(domainObject?.paxes || []);

    const [showGeneral, setShowGeneral] = useState('regular'); // minimized | regular
    const [showFullGeneral, setShowFullGeneral] = useState(false); // minimized | regular
    const [showInternational, setShowInternational] = useState('regular'); // minimized | regular
    const [showInternationalRestaurant, setShowInternationalRestaurant] = useState('regular'); // minimized | regular

    const [showHistoryModal, setShowHistoryModal] = useState(true);
    const [showMapModal, setShowMapModal] = useState(true);
    const modalHistoryRef = useRef(null);
    const modalMapRef = useRef(null);



    // Actualiza el formulario con los datos del objeto de dominio
    // Se utiliza posterior a la carga de imagenes desde ImagePicker ejecutando el metodo refresh()
    useEffect(() => {
        if (domainObject && domainObject.id && domainObject.langs) {
            reset({
                id: domainObject?.id,
                uid: domainObject?.uid,
                code: domainObject?.code ,
                status: domainObject?.status,
                needReservation: domainObject?.needReservation,
                colorMarkerMap: domainObject?.colorMarkerMap,
                iconMap: domainObject?.iconMap,
                latitude: domainObject?.latitude ?? 0.0,
                longitude: domainObject?.longitude ?? 0.0,
                level: domainObject?.level,
                showOrder: domainObject?.showOrder,
                houseId: domainObject?.houseId,
                floors: domainObject?.floors,
                totalSuits: domainObject?.totalSuits,
                menuFontColor: domainObject?.menuFontColor,
                menuBackgroundColor: domainObject?.menuBackgroundColor,
                menuBackground: domainObject?.menuBackground,
                menuLogo: domainObject?.menuLogo,
                menuSignature: domainObject?.menuSignature,
                openingTime: domainObject?.openingTime,
                closingTime: domainObject?.closingTime,
            })
            setLangs(domainObject.langs || []);
            setStatus(domainObject.status);
        }
    }, [ domainObject ]);


    const showHistory = () => {
        setShowHistoryModal(true);
        modalHistoryRef.current.showModal(domainObject);
    }

    const showMap = () => {
        setShowMapModal(true);
        modalMapRef.current.showModal(mapConfig);
    }

    const validationSchema = Yup.object().shape({
        code: Yup.string().required( REQUIRED )
    });

    const {register, handleSubmit, reset, errors} = useForm({
        validationSchema: validationSchema,
        defaultValues: {
            id: domainObject?.id,
            uid: domainObject?.uid,
            code: domainObject?.code || (`place_` + getCurrentDateForCodeField()).toUpperCase(),
            status: domainObject?.status,
            needReservation: domainObject?.needReservation,
            colorMarkerMap: domainObject?.colorMarkerMap,
            iconMap: domainObject?.iconMap,
            latitude: domainObject?.latitude ?? 0.0,
            longitude: domainObject?.longitude ?? 0.0,
            level: domainObject?.level,
            showOrder: domainObject?.showOrder,
            houseId: domainObject?.houseId,
            floors: domainObject?.floors,
            totalSuits: domainObject?.totalSuits,
            menuFontColor: domainObject?.menuFontColor,
            menuBackgroundColor: domainObject?.menuBackgroundColor,
            menuBackground: domainObject?.menuBackground,
            menuLogo: domainObject?.menuLogo,
            menuSignature: domainObject?.menuSignature,
            openingTime: domainObject?.openingTime,
            closingTime: domainObject?.closingTime,
        }
    });

    const onSubmit = (data, _) => {
        let dataRequest = {
            fbDataBase :  { id : app?.fbDataBase?.id },
            item : {
                ...data,
                id: domainObject?.id,
                udn: {id: udn?.id},

                latitude: position?.lat,
                longitude: position?.lng,
                outsideUDN: outsideUDN,

                weather: weather,
                status: status,
                needReservation: needReservation,

                langs: langs,
                langsRestaurant : langsRestaurant,
            }
        };

        if (category)
            dataRequest.item.category = { id: category };

        if (contactCategory)
            dataRequest.item.contactCategory = { id: contactCategory };

        if (parentPlace)
            dataRequest.item.parentPlace = { id: parentPlace };

        if (typePlace)
            dataRequest.item.typePlace = { id: typePlace };

        if (paxes)
            dataRequest.item.paxes = paxes.map( p => p?.id )

        if (editMode) {
            let id = domainObject.id;
            dataRequest.item.id = id

            genericIndexedActions.update(path, placeService, id, dataRequest)
        } else {
            genericIndexedActions.save(path, placeService, dataRequest)
        }

        returnToList()
    };

    const onOutsideUdnChange = (_outsideUDN ) => {
        if (_outsideUDN) {
            setPosition({
                lat: 0.0,
                lng: 0.0
            })
        } else {
            setPosition(editMode ? {
                lat: parseFloat(domainObject?.latitude),
                lng: parseFloat(domainObject?.longitude)
            } : mapConfig?.mapOptions?.center );
        }

        setOutsideUDN(_outsideUDN)
    }
    const onLatChange = (value ) => {
        setPosition({
            ...position,
            lat : value
        })
    }

    const onLngChange = ( value ) => {
        setPosition({
            ...position,
            lng : value
        })
    }

    const confirmDelete = (data) => {
        return ConfirmDelete({
            path,
            remove : genericIndexedActions.delete,
            data : data,
            fbDataBaseId :  app?.fbDataBase?.id,
            service: placeService
        } );
    };

    const returnToList = () => {
        reset();
        props.setItem(path, null);
        props.setForm(path, false, false, false);
        props.onClose();
    }

    const refreshObject = () => genericIndexedActions.get(path, placeService, domainObject?.id, app?.fbDataBase?.id);

    const getForm = () => {
        return (
            <Card className={"form_data"}>
                <CardTitle className="bg-light border-bottom p-3 mb-0">
                    <i className="far fa-edit mr-2"/>{FORM}
                    <div className="float-right">
                        <GenericButton
                            style={'table'}
                            toolTip={showFullGeneral ? 'Ocultar formulario completo' : 'Ver formulario completo' }
                            icon={showFullGeneral ? 'fa-eye-slash' : 'fa-eye'}
                            permission={'search'}
                            onClick={_ => setShowFullGeneral(!showFullGeneral)}
                        />
                        <GenericButton
                            style={'table'}
                            toolTip={showGeneral === 'regular' ? 'Minimizar' : 'Restaurar' }
                            icon={showGeneral === 'regular' ? 'fa-window-minimize' : 'fa-window-restore' }
                            permission={'search'}
                            onClick={_ => setShowGeneral(showGeneral === 'regular' ? 'minimized' : 'regular')}
                        />
                    </div>
                </CardTitle>
                <CardBody>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{ FL_CODE }</Label>
                                <Input
                                    type="text"
                                    name="code"
                                    placeholder={ PH_CODE }
                                    innerRef={register}
                                    className={(errors.code ? ' is-invalid' : '')}
                                />
                                { errors.code && <div className="invalid-feedback" style={{'display':'block'}}>{errors?.code?.message}</div>}
                            </FormGroup>
                        </Col>
                        <Col md="1"></Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{ FL_RESERVATION_NEEDED }</Label>
                                <br/>
                                <Switch
                                    animate={true}
                                    offText={"No"}
                                    onText={"Si"}
                                    value={ needReservation }
                                    onChange={ _ => setNeedReservation(!needReservation) }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>{ FL_STATUS }</Label>
                                <br/>
                                <Switch
                                    animate={true}
                                    offText={"No"}
                                    onText={"Si"}
                                    value={ status }
                                    onChange={ _ => setStatus(!status) }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>Ubicado en</Label>
                                <br/>
                                <SelectGeneric
                                    initialValue={ parentPlace }
                                    setExternalValue={ seParentPlace }
                                    provider={ placeProvider }
                                    customFilter={{ item : {udn: {id: udn.id}, categoryCodes : category}} }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>{ FL_CATEGORY }</Label>
                                <br/>
                                <SelectGeneric
                                    initialValue={ category }
                                    setExternalValue={ setCategory }
                                    provider={hotelCategoryProvider}
                                    getAllCategories={true}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>Tipo de lugar</Label>
                                <br/>
                                <SelectGeneric
                                    initialValue={ typePlace }
                                    setExternalValue={ setTypePlace }
                                    provider={ typePlaceProvider }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>Categoría de contacto</Label>
                                <br/>
                                <SelectGeneric
                                    initialValue={ contactCategory }
                                    setExternalValue={ setContactCategory }
                                    provider={hotelCategoryProvider}
                                    getAllCategories={true}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label >Latitud y longitud</Label>
                                <InputGroup>
                                    <Button color=" background-color-primary" onClick={ showMap } >
                                        <i className="fas fa-map-marker-alt text-white"/>
                                    </Button>
                                    <InputGroupAddon addonType="append">
                                        <Input type="number"
                                               name="lat"
                                               value={position?.lat}
                                               className={'lat-lng-input'}
                                               step={'0.0000001'}
                                               onChange={e => onLatChange(e.target.value)}
                                        />
                                    </InputGroupAddon>
                                    <InputGroupAddon addonType="append">
                                        <Input type="number"
                                               name="lng"
                                               className={'lat-lng-input'}
                                               value={ position?.lng}
                                               step={'0.0000001'}
                                               onChange={e => onLngChange(e.target.value)}
                                        />
                                    </InputGroupAddon>
                                </InputGroup>
                                <ErrorMessage errors={errors} name="latitude" component="div"
                                              className="invalid-feedback"/>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Color</Label>
                                <Input
                                    type="color"
                                    name="colorMarkerMap"
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Icono</Label>
                                <Input
                                    type="text"
                                    name="iconMap"
                                    placeholder="Icono"
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col  md="4">
                            <FormGroup>
                                <Label>Orden</Label>
                                <Input
                                    type="number"
                                    name="showOrder"
                                    min="1"
                                    placeholder="Orden"
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Nivel</Label>
                                <Input
                                    type="text"
                                    name="level"
                                    placeholder="Nivel"
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>{LB_FLOORS}</Label>
                                <Input
                                    type="number"
                                    name="floors"
                                    placeholder= {LB_FLOORS}
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>{LB_TOTAL_SUITS}</Label>
                                <Input
                                    type="number"
                                    name="totalSuits"
                                    placeholder= {LB_TOTAL_SUITS}
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col  md="4">
                            <FormGroup>
                                <Label>Clima</Label>
                                <br/>
                                <Switch
                                    animate={true}
                                    offText={"No"}
                                    onText={"Si"}
                                    value={ weather }
                                    onChange={ _ => setWeather(!weather) }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Color de fuente del menu</Label>
                                <Input
                                    type="color"
                                    name="menuFontColor"
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Color de fondo del menu</Label>
                                <Input
                                    type="color"
                                    name="menuBackgroundColor"
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>{'Imagen de fondo de Menus'}</Label>
                                <Input
                                    type="text"
                                    name="menuBackground"
                                    placeholder= {''}
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>{'Logo'}</Label>
                                <Input
                                    type="text"
                                    name="menuLogo"
                                    placeholder= {''}
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>{'Firma del Chef'}</Label>
                                <Input
                                    type="text"
                                    name="menuSignature"
                                    placeholder= {''}
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row></Row>
                </CardBody>
            </Card>
        )
    }

    const formHotel = () => {
        return <>
            <Card className={"form_data"}>
                <CardTitle className="bg-light border-bottom p-3 mb-0">
                    <i className="far fa-edit mr-2"/>{FORM}
                    <div className="float-right">
                        <GenericButton
                            style={'table'}
                            toolTip={showFullGeneral ? 'Ocultar formulario completo' : 'Ver formulario completo' }
                            icon={showFullGeneral ? 'fa-eye-slash' : 'fa-eye'}
                            permission={'search'}
                            onClick={_ => setShowFullGeneral(!showFullGeneral)}
                        />
                        <GenericButton
                            style={'table'}
                            toolTip={showGeneral === 'regular' ? 'Minimizar' : 'Restaurar' }
                            icon={showGeneral === 'regular' ? 'fa-window-minimize' : 'fa-window-restore' }
                            permission={'search'}
                            onClick={_ => setShowGeneral(showGeneral === 'regular' ? 'minimized' : 'regular')}
                        />
                    </div>
                </CardTitle>
                <Collapse isOpen={showGeneral === 'regular'} >
                    <CardBody>
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label>Código</Label>
                                    <Input
                                        type="text"
                                        name="code"
                                        placeholder="Código"
                                        innerRef={register}
                                        className={(errors.code ? ' is-invalid' : '')}
                                    />
                                    { errors.code && <div className="invalid-feedback" style={{'display':'block'}}>{errors?.code?.message}</div>}
                                </FormGroup>
                            </Col>

                            <Col md="3">
                                <FormGroup>
                                    <Label>Tipo de lugar</Label>
                                    <br/>
                                    <SelectGeneric
                                        initialValue={ typePlace }
                                        setExternalValue={ setTypePlace }
                                        provider={ typePlaceProvider }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label>Categoría de contacto</Label>
                                    <br/>
                                    <SelectGeneric
                                        initialValue={ contactCategory }
                                        setExternalValue={ setContactCategory }
                                        provider={hotelCategoryProvider}
                                        getAllCategories={true}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup>
                                    <Label>Color</Label>
                                    <Input
                                        type="color"
                                        name="colorMarkerMap"
                                        innerRef={register}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label>Categoría</Label>
                                    <br/>
                                    <SelectGeneric
                                        initialValue={ category }
                                        setExternalValue={ setCategory }
                                        provider={hotelCategoryProvider}
                                        getAllCategories={true}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label>Lugar donde se ubica</Label>
                                    <br/>
                                    <SelectGeneric
                                        initialValue={ parentPlace }
                                        setExternalValue={ seParentPlace }
                                        provider={ placeProvider }
                                        customFilter={{ item : {udn: {id: udn.id}}} }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="8">
                                <FormGroup>
                                    <Label >Latitud y longitud</Label>
                                    <InputGroup>
                                        <Button color=" background-color-primary" onClick={ showMap } >
                                            <i className="fas fa-map-marker-alt text-white"/>
                                        </Button>
                                        <InputGroupAddon addonType="append">
                                            <Input type="number"
                                                   name="lat"
                                                   value={position?.lat}
                                                   className={'lat-lng-input'}
                                                   step={'0.0000001'}
                                                   onChange={e => onLatChange(e.target.value)}
                                            />
                                        </InputGroupAddon>
                                        <InputGroupAddon addonType="append">
                                            <Input type="number"
                                                   name="lng"
                                                   className={'lat-lng-input'}
                                                   value={ position?.lng}
                                                   step={'0.0000001'}
                                                   onChange={e => onLngChange(e.target.value)}
                                            />
                                        </InputGroupAddon>
                                    </InputGroup>
                                    <ErrorMessage errors={errors} name="latitude" component="div"
                                                  className="invalid-feedback"/>
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label>Icono</Label>
                                    <Input
                                        type="text"
                                        name="iconMap"
                                        placeholder="Icono"
                                        innerRef={register}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="1">
                                <FormGroup>
                                    <Label>Nivel</Label>
                                    <Input
                                        type="text"
                                        name="level"
                                        placeholder="Nivel"
                                        innerRef={register}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="2">
                                <FormGroup>
                                    <Label>{LB_FLOORS}</Label>
                                    <Input
                                        type="number"
                                        name="floors"
                                        placeholder= {LB_FLOORS}
                                        innerRef={register}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup>
                                    <Label>{LB_TOTAL_SUITS}</Label>
                                    <Input
                                        type="number"
                                        name="totalSuits"
                                        placeholder= {LB_TOTAL_SUITS}
                                        innerRef={register}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup>
                                    <Label>{LB_OPENING_TIME}</Label>
                                    <Input
                                        type="time"
                                        name="openingTime"
                                        placeholder= {LB_OPENING_TIME}
                                        innerRef={register}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup>
                                    <Label>{LB_CLOSING_TIME}</Label>
                                    <Input
                                        type="time"
                                        name="closingTime"
                                        placeholder= {LB_CLOSING_TIME}
                                        innerRef={register}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col  md="3">
                                <FormGroup>
                                    <Label>Orden</Label>
                                    <Input
                                        type="number"
                                        name="showOrder"
                                        min="1"
                                        placeholder="Orden"
                                        innerRef={register}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup>
                                    <Label>Activo</Label>
                                    <br/>
                                    <Switch
                                        animate={true}
                                        offText={"No"}
                                        onText={"Si"}
                                        value={ status }
                                        onChange={ _ => setStatus(!status) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col  md="2">
                                <FormGroup>
                                    <Label>Clima</Label>
                                    <br/>
                                    <Switch
                                        animate={true}
                                        offText={"No"}
                                        onText={"Si"}
                                        value={ weather }
                                        onChange={ _ => setWeather(!weather) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup>
                                    <Label>Necesita reservación</Label>
                                    <br/>
                                    <Switch
                                        animate={true}
                                        offText={"No"}
                                        onText={"Si"}
                                        value={ needReservation }
                                        onChange={ _ => setNeedReservation(!needReservation) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup>
                                    <Label>Restaurante</Label>
                                    <br/>
                                    <Switch
                                        animate={true}
                                        offText={"No"}
                                        onText={"Si"}
                                        value={ showLangRest }
                                        onChange={ (item, val) => setShowLangRest(val) }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Collapse isOpen={showFullGeneral} >
                            <Row>

                            </Row>
                        </Collapse>
                    </CardBody>
                </Collapse>
            </Card>
        </>
    }

    const formEvents = () => {
        return <>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label>Código</Label>
                        <Input
                            type="text"
                            name="code"
                            placeholder="Código"
                            innerRef={register}
                            className={(errors.code ? ' is-invalid' : '')}
                        />
                        { errors.code && <div className="invalid-feedback" style={{'display':'block'}}>{errors?.code?.message}</div>}
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label>Activo</Label>
                        <br/>
                        <Switch
                            animate={true}
                            offText={"No"}
                            onText={"Si"}
                            value={ status }
                            onChange={ _ => setStatus(!status) }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="2">
                    <FormGroup>
                        <Label>{'Fuera de UDN'}</Label>
                        <br/>
                        <Switch
                            animate={true}
                            offText={"No"}
                            onText={"Si"}
                            value={ outsideUDN }
                            onChange={ _ => onOutsideUdnChange(!outsideUDN) }
                        />
                    </FormGroup>
                </Col>
                <Col md="8">
                    <FormGroup>
                        <Label >Latitud y longitud</Label>
                        <InputGroup>
                            <Button color=" background-color-primary" onClick={ showMap }
                                    disabled={outsideUDN}
                            >
                                <i className="fas fa-map-marker-alt text-white"/>
                            </Button>
                            <InputGroupAddon addonType="append">
                                <Input type="number"
                                       name="lat"
                                       value={position?.lat}
                                       className={'lat-lng-input'}
                                       step={'0.0000001'}
                                       disabled={outsideUDN}
                                       onChange={e => onLatChange(e.target.value)}
                                />
                            </InputGroupAddon>
                            <InputGroupAddon addonType="append">
                                <Input type="number"
                                       name="lng"
                                       className={'lat-lng-input'}
                                       value={ position?.lng}
                                       step={'0.0000001'}
                                       disabled={outsideUDN}
                                       onChange={e => onLngChange(e.target.value)}
                                />
                            </InputGroupAddon>
                        </InputGroup>
                        <ErrorMessage errors={errors} name="latitude" component="div"
                                      className="invalid-feedback"/>
                    </FormGroup>
                </Col>
            </Row>
        </>
    }

    const getInternational = () => {
        return (
            <>
                <div className={'form-container'}>
                    <div className={''}>
                        <h2>{INTERNATIONAL}</h2>
                    </div>
                    <div className={''}>
                        <Row>
                            <Col >
                                <PlaceLangs appCode={app?.code} langs={langs} setLangs={setLangs} translatableLangs={translatableLangs}/>
                            </Col>
                        </Row>
                    </div>
                </div>

            </>
        )
    }

    const getInternationalRestaurant = () => {
        return (
            <>
                <div className={'form-container'}>
                    <div className={''}>
                        <h2>{INTERNATIONAL}</h2>
                    </div>
                    <div className={''}>
                        <Row>
                            <Col >
                                <PlaceRestaurantLangs langs={langsRestaurant} setLangs={setLangsRestaurant}
                                                      translatableLangs={domainObject?.translatedLangRest || []}/>
                            </Col>
                        </Row>
                    </div>
                </div>

            </>
        )
    }

    return (<>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Card className={'card-modal-big'}>
                    <CardTitle className="bg-light border-bottom p-3 mb-0">
                        {
                            editMode ?  <>
                                    <h2 className={'card-modal-title'} > { app.name + '  /  ' + udn.value + ' / ' + ' Lugares / ' + domainObject?.title?.toUpperCase() }</h2>
                                    <br />
                                    <h5 className={'card-modal-uid'} > { '     uid: ' + domainObject?.uid }</h5>
                                </> :
                                <>
                                    <h2 className={'card-modal-title'} > { app.name + '  /  ' + udn.value + ' / ' + ' Lugares / Nuevo '  }</h2>
                                    <br />
                                    <h5 className={'card-modal-uid'} > {  }</h5>
                                </>
                        }
                        <div className="top-right">
                            <GenericButton
                                style={'table'}
                                icon={'fa-save'}
                                toolTip={TT_SAVE}
                                permission={'add'}
                                type={'submit'}
                            />
                            {
                                editMode &&
                                <>
                                    <Separator/>
                                    <GenericButton
                                        style={'table'}
                                        toolTip={TT_HISTORY}
                                        icon={'fa-clock'}
                                        permission={'search'}
                                        onClick={showHistory}
                                    />
                                    <TranslateButton item={domainObject} service={placeService} path={path}/>
                                    <GenericButton
                                        style={'danger-secondary'}
                                        toolTip={TT_DELETE}
                                        icon={'fa-trash'}
                                        permission={'delete'}
                                        onClick={_ => confirmDelete(domainObject)}
                                    />
                                </>
                            }
                            <Separator/>
                            <GenericButton
                                style={'danger-secondary'}
                                toolTip={TT_CLOSE}
                                icon={'fa-times'}
                                permission={'search'}
                                onClick={returnToList}
                            />
                        </div>
                    </CardTitle>
                    <CardBody className={'card-body-modal'}>
                            <Row>
                                <Col md="8">
                                    {(
                                        function() {
                                    switch (app.code) {
                                        case "HCDP":
                                            return formHotel()
                                        case "HOTEL":
                                            return formHotel()
                                        case "EVENTS" :
                                            return formEvents()
                                        default:
                                            return getForm();
                                                }
                                            })()}
                                </Col>
                                <Col md="4">
                                    <ImagePicker 
                                        className={'place'} 
                                        title={'Media'} 
                                        app={app} 
                                        service={placeService} 
                                        baseMediaService={placeMediaService}
                                        objectMedia={domainObject}
                                        afterUpload={refreshObject}
                                    />
                                    
                                </Col>
                            </Row>
                        { getInternational() }
                        { showLangRest && getInternationalRestaurant() }
                    </CardBody>
                </Card>
            </Form>
            {
                showMapModal && <MapPickerModal
                    ref={modalMapRef}
                    setPosition={setPosition}
                    initialPosition={position}
                    googleConfig={mapConfig}
                    updateMarkerPosition={true}
                />
            }
            {
                showHistoryModal && <LogHistoryModal
                    ref={ modalHistoryRef }
                    item={ domainObject }
                />
            }
        </>
    )
}

const mapStateToProps = state => ({
})

const mapActionsToProps = ({
    setItem,
    setForm,
    syncGallery
})

export default connect(mapStateToProps, mapActionsToProps)(PlaceForm)
